import React from 'react'
import { useSelector } from 'react-redux'
import * as R from 'ramda'

import { AtsLockContext } from 'components/common/AtsLock'

import { useSendTestsInvitationsModal } from 'components/modals/SendTestsInvitationsModal/hooks'
import { useSendTestsRemindersModal } from 'components/modals/SendTestsRemindersModal/hooks'
import { jobs } from 'orm'
import {
  useCandidateActions,
  useCandidateGroupActions,
  useCandidateState,
  useJobDetailsTab,
  useFilter,
} from './../hooks'

const useJobInvitesTabData = state => {
  const { projectId } = state

  const data = useSelector(state => state.orm.candidates)
  const job = useSelector(state => jobs.getItem()(state, { pk: projectId }))

  const { items, meta: { selected } = {} } = data ?? {}

  const { invitees, remindees } = React.useMemo(
    () =>
      R.pipe(
        R.values,
        R.reduceBy(
          (acc, candidate) =>
            !R.isEmpty(selected)
              ? R.includes(candidate?.id, R.keys(selected))
                ? [...acc, candidate]
                : acc
              : [...acc, candidate],
          [],
          ({ status }) =>
            R.equals(status, 'created') ? 'invitees' : 'remindees',
        ),
      )(items),
    [items, selected],
  )

  const deadline = R.propOr('', 'deadline', job)

  const isNoInvitees = R.or(R.isEmpty(invitees), R.isNil(invitees))
  const isNoRemindees = R.or(R.isEmpty(remindees), R.isNil(remindees))

  return {
    state: { invitees, remindees, deadline, isNoInvitees, isNoRemindees },
  }
}

export const useJobInvitesTab = state => {
  const { projectId } = state

  const { isAtsLocked } = React.useContext(AtsLockContext)

  const status = ['created', 'invited']

  const {
    state: { invitees, remindees, deadline, isNoInvitees, isNoRemindees },
  } = useJobInvitesTabData({ projectId })

  const {
    state: { count, search },
    handlers: { setSearch },
  } = useJobDetailsTab()

  const {
    state: { candidate, selected, hasMoveCandidatesFeature },
    handlers: { setCandidate },
  } = useCandidateState()

  const {
    state: { filter },
    handlers: { setFilter },
  } = useFilter()

  const {
    handleSendTestsInvitationsModalClose,
    handleSendTestsInvitationsModalConfirm,
    handleSendTestsInvitationsModalOpen,
    isSendTestsInvitationsModalOpen,
  } = useSendTestsInvitationsModal(
    { projectId },
    { handleSetCandidate: setCandidate },
  )

  const {
    handleSendTestsRemindersModalClose,
    handleSendTestsRemindersModalConfirm,
    handleSendTestsRemindersModalOpen,
    isSendTestsRemindersModalOpen,
  } = useSendTestsRemindersModal(
    { projectId },
    { handleSetCandidate: setCandidate },
  )

  const {
    state: {
      isCandidateManagementModalOpen,
      isDeleteCandidateModalOpen,
      isMoveCandidateModalOpen,
    },
    handlers: {
      getActions,
      handleFavorite,
      handleCandidateManagementModalClose,
      handleCandidateManagementModalConfirm,
      handleDeleteCandidateModalClose,
      handleDeleteCandidateModalConfirm,
      handleMoveCandidateModalClose,
      handleMoveCandidateModalConfirm,
    },
  } = useCandidateActions(
    { candidate, projectId, status, hasMoveCandidatesFeature, isAtsLocked },
    { handleSetCandidate: setCandidate },
  )

  const {
    state: {
      actions,
      isDeleteCandidateGroupModalOpen,
      isExportCandidateGroupModalOpen,
      isMoveCandidateGroupModalOpen,
    },
    handlers: {
      handleDeleteCandidateGroupModalClose,
      handleDeleteCandidateGroupModalConfirm,
      handleExportCandidateGroupModalClose,
      handleMoveCandidateGroupModalClose,
      handleMoveCandidateGroupModalConfirm,
    },
  } = useCandidateGroupActions({
    projectId,
    selected,
    status,
    hasMoveCandidatesFeature,
    isAtsLocked,
  })

  const handleSendTestsMessage = React.useCallback(
    ({ candidate }) => {
      setCandidate(candidate)
      R.equals(candidate?.status, 'created')
        ? handleSendTestsInvitationsModalOpen()
        : handleSendTestsRemindersModalOpen()
    },
    [
      handleSendTestsInvitationsModalOpen,
      handleSendTestsRemindersModalOpen,
      setCandidate,
    ],
  )

  return {
    state: {
      actions,
      candidate,
      count,
      deadline,
      invitees,
      remindees,
      search,
      hasMoveCandidatesFeature,
      isAtsLocked,
      filter,
      isCandidateManagementModalOpen,
      isDeleteCandidateModalOpen,
      isMoveCandidateModalOpen,
      isDeleteCandidateGroupModalOpen,
      isExportCandidateGroupModalOpen,
      isMoveCandidateGroupModalOpen,
      isSendTestsInvitationsModalOpen,
      isSendTestsRemindersModalOpen,
      isNoInvitees,
      isNoRemindees,
    },
    handlers: {
      getActions,
      setFilter,
      handleFavorite,
      handleCandidateManagementModalClose,
      handleCandidateManagementModalConfirm,
      handleDeleteCandidateModalClose,
      handleDeleteCandidateModalConfirm,
      handleMoveCandidateModalClose,
      handleMoveCandidateModalConfirm,
      handleDeleteCandidateGroupModalClose,
      handleDeleteCandidateGroupModalConfirm,
      handleExportCandidateGroupModalClose,
      handleMoveCandidateGroupModalClose,
      handleMoveCandidateGroupModalConfirm,
      handleSendTestsInvitationsModalClose,
      handleSendTestsInvitationsModalConfirm,
      handleSendTestsInvitationsModalOpen,
      handleSendTestsRemindersModalClose,
      handleSendTestsRemindersModalConfirm,
      handleSendTestsRemindersModalOpen,
      handleSendTestsMessage,
      setSearch,
    },
  }
}
